import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CvRendu, DemandeDossierArtiste, SaisieCv } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';
import type { ReponseDocumentSigne } from './documentsGalerie.js';

const racine = (g: string, id: string) =>
  `/g/${encodeURIComponent(g)}/artistes/${encodeURIComponent(id)}`;
export function useCvArtiste(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'cv-artiste', id),
    queryFn: (contexte) =>
      api.get<CvRendu>(`${racine(g, id)}/cv`, { galerie: g, ...appelDeRequete(contexte) }),
  });
}
export type GesteCv =
  | { type: 'ajouter'; corps: SaisieCv }
  | { type: 'modifier'; id: string; corps: Omit<SaisieCv, 'pays'> & { pays: string | null } }
  | { type: 'supprimer'; id: string }
  | { type: 'masquer'; expositionId: string }
  | { type: 'texte' };
export function useModifierCv(g: string, id: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (geste: GesteCv, appel: AppelApi) => {
        const url = `${racine(g, id)}/cv`;
        const options = { galerie: g, ...appel };
        switch (geste.type) {
          case 'ajouter':
            return api.post(url, geste.corps, options);
          case 'modifier':
            return api.patch(`${url}/${encodeURIComponent(geste.id)}`, geste.corps, options);
          case 'supprimer':
            return api.delete(`${url}/${encodeURIComponent(geste.id)}`, options);
          case 'masquer':
            return api.put(
              `${url}/masquage`,
              { expositionId: geste.expositionId, masquee: true },
              options,
            );
          case 'texte':
            return api.post(`${url}/texte`, {}, options);
        }
      },
      onSuccess: async () => {
        await Promise.all([
          client.invalidateQueries({ queryKey: cleMetier(g, 'cv-artiste', id) }),
          client.invalidateQueries({ queryKey: cleMetier(g, 'artistes') }),
        ]);
      },
    }),
  );
}
export function useDossierArtiste(g: string, id: string) {
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: DemandeDossierArtiste, appel: AppelApi) =>
        api.post<ReponseDocumentSigne>(`${racine(g, id)}/dossier`, corps, { galerie: g, ...appel }),
    }),
  );
}
